import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import { useState } from 'react'
import EmailDeliverabilityPill from 'src/components/EmailDeliverability/Pill'
import { LANGUAGES_LABEL_MAP } from 'src/constants/languages'
import useLazyNamespace from 'src/hooks/useLazyNamespace'
import useTrackEvent from 'src/hooks/useTrackEvent'
import { useAuthProvider } from '../../context/AuthProvider'
import { IUser } from '../../types/graphql'
import { RedirectUrls } from '../../types/types'
import BaseButton from '../Buttons/BaseButton'
import ImageWithFallback from '../ImageWithFallback'
import ReportUserModal from '../ReportUserModal'
import ProfileLocationCard from './ProfileLocationCard'
import SocialMediaIcons from './SocialMediaIcons'
import VerificationBadge from './VerificationBadge'

const AdminEdit = dynamic(() => import('../UserProfile/AdminEditUser'))
const Reviews = dynamic(() => import('../Review/Reviews'))

type IProps = {
  user: IUser
  isBusiness?: boolean
  teamId?: string
  locationId: string | null
  configurationId: string | null
}

const ABOUT_PREVIEW_LENGTH = 220

const getJoinYear = (createdOn: string | null | undefined): string => {
  if (!createdOn) return ''
  const date = new Date(createdOn)
  if (Number.isNaN(date.getTime())) return ''
  return String(date.getFullYear())
}

export default function UserProfile({
  user,
  isBusiness,
  teamId,
  locationId,
  configurationId
}: IProps) {
  const [reportModalOpen, setReportModalOpen] = useState(false)
  const [descExpanded, setDescExpanded] = useState(false)
  const { t } = useTranslation('user')
  // `admin` namespace is omitted from these pages' SSR translations (it's only
  // needed when an admin opens the profile, to render <AdminEdit>). Lazy-load
  // it client-side and gate the admin section on `adminNsReady` below. The
  // public verification labels were moved to the `user` namespace so this page
  // no longer needs `admin` for anything renters see.
  // See docs/performance/large-page-data.md.
  const adminNsReady = useLazyNamespace('admin')

  const { currentUser, toggleLoginModal } = useAuthProvider()
  const { trackEvent, EventAction } = useTrackEvent()
  const {
    firstname,
    lastname,
    createdOn,
    listings,
    description,
    isVerified,
    id,
    userChanges,
    profilePicture,
    job,
    languages,
    hobbie,
    favoriteSong,
    currentCity,
    movieAsLife
  } = user
  const userPhoto = profilePicture?.url
  const listingsUser = listings?.hits ?? []
  const listingsCount = listings?.total ?? listingsUser.length
  const reviewsTotal = user.reviews?.total ?? 0
  const avgRating = user.reviews?.avgRating

  const locIdParam = locationId ? `?location=${locationId}` : ''
  const configIdParam = configurationId ? `&config=${configurationId}` : ''
  const messageHref = teamId
    ? `${RedirectUrls.RentMessages}/recipient/team/${teamId}` +
      locIdParam +
      configIdParam
    : `${RedirectUrls.RentMessages}/recipient/${id}` +
      locIdParam +
      configIdParam
  const encodedRedirect = encodeURIComponent(messageHref)
  const contactHref = currentUser
    ? currentUser?.isVerified || currentUser?.requestedVerification
      ? messageHref
      : `${RedirectUrls.UserConfirmation}?redirectU=${encodedRedirect}` +
        (locationId ? `&locationId=${locationId}` : '') +
        (configurationId ? `&configurationId=${configurationId}` : '')
    : undefined

  const handleOnContactClick = () => {
    if (locationId && configurationId) {
      trackEvent({
        action: EventAction.AddToCart,
        locationId,
        configurationId
      })
      trackEvent({
        action: EventAction.Contact,
        locationId,
        configurationId
      })
    }
    if (!currentUser) {
      toggleLoginModal(true, 'chat_host')
    }
  }

  const fname =
    firstname ?? userChanges?.firstname ?? t('PROFILE.ANONYMOUS_NAME')
  const fullName = `${fname}${lastname ? ` ${lastname}` : ''}`
  // Default placeholders we treat as "no real asset" — avatar.png (user) and
  // avatarb.png (team contact fallback). Forces the monogram path so we never
  // hero a generic placeholder.
  const isPlaceholderAsset =
    !userPhoto || /\/avatar(b)?\.(png|jpe?g|webp)$/i.test(userPhoto)
  const hasRealAsset = !isPlaceholderAsset
  const firstInitial = (fname?.trim()?.charAt(0) || '·').toUpperCase()
  const joinYear = getJoinYear(createdOn)
  const eyebrow = isBusiness
    ? t('PROFILE.EYEBROW_BUSINESS')
    : t('PROFILE.EYEBROW_HOST')
  const accentCity = currentCity ?? user.address?.city
  const aboutHeading = isBusiness
    ? t('PROFILE.ABOUT_BUSINESS_HEADING')
    : t('PROFILE.ABOUT_HOST_HEADING')
  const listingsHeading = isBusiness
    ? t('PROFILE.LISTINGS_HEADING_BUSINESS')
    : t('PROFILE.LISTINGS_HEADING_HOST')
  const listingsIntro = isBusiness
    ? t('PROFILE.LISTINGS_INTRO_BUSINESS')
    : t('PROFILE.LISTINGS_INTRO_HOST')

  const personalDetails: {
    icon: string
    label: string
    value?: string | null
  }[] = [
    { icon: 'si-job', label: t('BIO.OCCUPATION_LABEL'), value: job },
    {
      icon: 'si-language',
      label: t('BIO.LANGUAGES_LABEL'),
      value:
        languages && languages.length > 0
          ? languages.map((lang) => LANGUAGES_LABEL_MAP[lang]).join(', ')
          : null
    },
    {
      icon: 'si-socialnetwork',
      label: t('BIO.LIVES_IN_LABEL'),
      value: currentCity
    },
    { icon: 'si-hobbie', label: t('BIO.FREE_TIME_LABEL'), value: hobbie },
    {
      icon: 'si-song',
      label: t('BIO.FAVORITE_SONG_LABEL'),
      value: favoriteSong
    },
    {
      icon: 'si-movie',
      label: t('BIO.FAVORITE_MOVIE_LABEL'),
      value: movieAsLife
    }
  ]
  const visiblePersonalDetails = personalDetails.filter((d) => d.value)

  const verificationItems = [
    { label: t('PROFILE.VERIFICATION_PHONE'), verified: !!isVerified },
    { label: t('PROFILE.VERIFICATION_EMAIL'), verified: !!isVerified },
    { label: t('PROFILE.VERIFICATION_ID_LEGAL'), verified: !!isVerified }
  ]

  const showAboutToggle =
    !!description && description.length > ABOUT_PREVIEW_LENGTH
  const aboutText =
    description &&
    (descExpanded || description.length <= ABOUT_PREVIEW_LENGTH
      ? description
      : `${description.slice(0, ABOUT_PREVIEW_LENGTH).trimEnd()}…`)

  const isOwnProfile = currentUser?.id === id

  return (
    <div className="bg-white-500">
      {/* ── Editorial header ── */}
      <header className="border-b border-ink-100/15 bg-cream-300">
        <div className="mx-auto w-full max-w-7xl px-5 pt-[calc(var(--nav-h)+40px)] pb-12 md:px-10 md:pt-[calc(var(--nav-h)+64px)] md:pb-20">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className="relative inline-flex size-2"
                >
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-blue-300 opacity-70" />
                  <span className="relative inline-flex size-2 rounded-full bg-blue-500" />
                </span>
                <span className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                  {eyebrow}
                </span>
              </div>

              <h1 className="mt-6 font-display text-[32px] leading-[1.05] font-medium tracking-[-0.01em] break-words text-ink-700 md:mt-8 md:text-[60px] lg:text-[68px]">
                <span>{fullName}</span>
                {accentCity && (
                  <>
                    {' '}
                    <span className="font-display text-blue-500 italic">
                      {t('PROFILE.IN_CITY', { city: accentCity })}
                    </span>
                  </>
                )}
              </h1>

              {/* Meta strip — hairline-separated editorial caption */}
              <dl className="mt-8 flex flex-wrap items-baseline gap-x-6 gap-y-3 border-t border-ink-100/15 pt-6 md:mt-10 md:gap-x-10">
                {joinYear && (
                  <div className="flex flex-col gap-0.5">
                    <dt className="font-display text-[10px] tracking-[0.18em] text-ink-200 uppercase md:text-[11px]">
                      {isBusiness
                        ? t('PROFILE.MEMBER_SINCE_BUSINESS', { year: joinYear })
                        : t('PROFILE.MEMBER_SINCE', { year: joinYear })}
                    </dt>
                    <dd className="font-display text-[26px] leading-none font-medium tracking-[-0.01em] text-ink-700 italic md:text-[32px]">
                      {joinYear}
                    </dd>
                  </div>
                )}
                {listingsCount > 0 && (
                  <div className="flex flex-col gap-0.5">
                    <dt className="font-display text-[10px] tracking-[0.18em] text-ink-200 uppercase md:text-[11px]">
                      {t('PROFILE.LISTINGS_COUNT', {
                        count: listingsCount
                      })}
                    </dt>
                    <dd className="font-display text-[26px] leading-none font-medium tracking-[-0.01em] text-ink-700 italic md:text-[32px]">
                      {listingsCount}
                    </dd>
                  </div>
                )}
                {reviewsTotal > 0 && avgRating != null && (
                  <div className="flex flex-col gap-0.5">
                    <dt className="font-display text-[10px] tracking-[0.18em] text-ink-200 uppercase md:text-[11px]">
                      {t('PROFILE.REVIEWS_COUNT', {
                        count: reviewsTotal
                      })}
                    </dt>
                    <dd className="font-display text-[26px] leading-none font-medium tracking-[-0.01em] text-ink-700 italic md:text-[32px]">
                      {avgRating.toFixed(1)}
                      <span className="ml-1 text-[15px] text-ink-200 not-italic">
                        /5
                      </span>
                    </dd>
                  </div>
                )}
              </dl>

              {/* Identity verified inline + admin edit */}
              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-3">
                {isVerified && (
                  <span className="inline-flex items-center gap-2 text-[12px] tracking-[0.08em] text-blue-500 uppercase">
                    <svg
                      aria-hidden="true"
                      className="size-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.5 12.75l6 6 9-13.5"
                      />
                    </svg>
                    {t('PROFILE.VERIFIED_BADGE')}
                  </span>
                )}
                {!isBusiness && (
                  <SocialMediaIcons user={user} primaryColor="blue" />
                )}
                {currentUser?.isAdmin && !isBusiness && adminNsReady && (
                  <AdminEdit user={user} />
                )}
                {/* Deliverability badge. Deliberately NOT gated on
                    `currentUser?.isAdmin` like <AdminEdit> above: the server
                    already gated the field (it returns `status: "hidden"` to
                    anyone unauthorized), and re-gating on that narrow flag
                    would reintroduce the adminRole-only blindness the resolver
                    fixes. `adminNsReady` here is an i18n-readiness gate only —
                    the copy lives in the lazily-loaded `admin` namespace. */}
                {adminNsReady && (
                  <EmailDeliverabilityPill
                    deliverability={user.emailDeliverability}
                    variant="inline"
                  />
                )}
              </div>
            </div>

            {/* Right rail — small framed visual + contact CTA.
                Asset quality varies wildly (default avatars, low-res selfies,
                white-bg PNG logos). The frame stays small, the editorial
                weight lives in typography, and three render modes degrade
                gracefully:
                  · user with real portrait  → object-cover square
                  · business with real logo  → white-bg + object-contain (no crop)
                  · no asset (either kind)   → italic Fraunces monogram tile */}
            <div className="md:col-span-5">
              <div className="flex md:justify-end">
                {hasRealAsset && !isBusiness && (
                  <div className="relative size-[180px] shrink-0 overflow-hidden border border-ink-100/15 bg-white-500 md:size-[240px] lg:size-[280px]">
                    <ImageWithFallback
                      src={userPhoto!}
                      alt={fullName}
                      fill
                      style={{ objectFit: 'cover' }}
                      sizes="280px"
                    />
                    {accentCity && (
                      <span
                        aria-hidden="true"
                        className="absolute top-3 left-3 font-display text-[10px] tracking-[0.22em] text-white-500/90 uppercase md:text-[11px]"
                      >
                        {accentCity}
                      </span>
                    )}
                  </div>
                )}
                {hasRealAsset && isBusiness && (
                  <div className="relative flex size-[180px] shrink-0 items-center justify-center border border-ink-100/15 bg-white-500 p-6 md:size-[240px] md:p-8 lg:size-[280px]">
                    <ImageWithFallback
                      src={userPhoto!}
                      alt={fullName}
                      fill
                      style={{ objectFit: 'contain' }}
                      sizes="280px"
                    />
                  </div>
                )}
                {!hasRealAsset && (
                  <div
                    aria-hidden="true"
                    className="relative flex size-[180px] shrink-0 items-center justify-center overflow-hidden border border-ink-100/15 bg-cream-300 md:size-[240px] lg:size-[280px]"
                  >
                    <span className="font-display text-[120px] leading-none font-medium tracking-[-0.04em] text-blue-500 italic md:text-[160px] lg:text-[180px]">
                      {firstInitial}
                    </span>
                  </div>
                )}
              </div>
              {!isOwnProfile && (
                <div className="mt-6 border-t border-ink-100/15 pt-5">
                  <p className="text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
                    {t('PROFILE.CONTACT_INTRO', { name: fname })}
                  </p>
                  <div className="mt-4">
                    <BaseButton
                      href={contactHref}
                      onClick={handleOnContactClick}
                      fullWidth
                      variant="contained"
                      size="large"
                      fontWeight={600}
                      rounded
                    >
                      {t('PROFILE.CONTACT_CTA')}
                    </BaseButton>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── Body — 12-col rail ── */}
      <div className="mx-auto w-full max-w-7xl px-5 md:px-10">
        <div className="grid grid-cols-1 gap-10 py-16 md:grid-cols-12 md:gap-12 md:py-24">
          {/* Left rail — about + verification + response */}
          <aside className="md:col-span-4">
            {/* About */}
            <section>
              <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                {aboutHeading}
              </p>
              <h2 className="mt-3 font-display text-[24px] leading-[1.15] font-medium text-ink-700 md:text-[28px]">
                <span className="font-display italic">{fname}</span>
              </h2>
              <p className="mt-4 text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
                {aboutText ?? t('PROFILE.ABOUT_DEFAULT')}
              </p>
              {showAboutToggle && (
                <button
                  type="button"
                  onClick={() => setDescExpanded((v) => !v)}
                  className="mt-3 inline-flex items-center gap-2 text-[12px] font-medium tracking-[0.14em] text-ink-700 uppercase underline decoration-blue-500/40 underline-offset-[6px] transition-colors hover:text-blue-500 hover:decoration-blue-500"
                >
                  {descExpanded
                    ? t('PROFILE.SHOW_LESS')
                    : t('PROFILE.SHOW_MORE')}
                </button>
              )}

              {/* Personal details — only for non-business with content */}
              {!isBusiness && visiblePersonalDetails.length > 0 && (
                <div className="mt-8 border-t border-ink-100/15 pt-6">
                  <p className="font-display text-[11px] tracking-[0.18em] text-ink-200 uppercase">
                    {t('PROFILE.PERSONAL_HEADING')}
                  </p>
                  <ul className="mt-4 space-y-4">
                    {visiblePersonalDetails.map((item) => (
                      <li key={item.icon} className="flex items-start gap-3">
                        <i
                          aria-hidden="true"
                          className={`si ${item.icon} mt-0.5 w-4 shrink-0 text-blue-500`}
                        />
                        <div className="text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
                          <span className="text-ink-700">{item.label}</span>
                          {` ${item.value}`}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            {/* Verification */}
            <section className="mt-12 border-t border-ink-100/15 pt-12">
              <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                {t('PROFILE.VERIFICATION_HEADING')}
              </p>
              <p className="mt-4 max-w-xs text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
                {t('PROFILE.VERIFICATION_INTRO')}
              </p>
              <ul className="mt-6">
                {verificationItems.map((item, idx) => (
                  <VerificationBadge
                    key={item.label}
                    verified={item.verified}
                    label={item.label}
                    index={idx + 1}
                  />
                ))}
              </ul>

              {/* No response-time row: the old «≈ 6 horas» was never
                  measured (2026-10-07). Measured speed lives on the listing
                  as «Responde en menos de 24 h». */}
              {!isOwnProfile && (
                <button
                  type="button"
                  onClick={() => setReportModalOpen(true)}
                  className="mt-8 inline-flex items-center gap-2 text-[12px] tracking-[0.14em] text-ink-200 uppercase underline decoration-ink-100/40 underline-offset-[6px] transition-colors hover:text-ink-700 hover:decoration-ink-700/60"
                >
                  {t('PROFILE.REPORT_LABEL', { name: fname })}
                </button>
              )}
            </section>
          </aside>

          {/* Right rail — listings */}
          <div className="md:col-span-8">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-12">
              <div className="md:col-span-5">
                <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                  {listingsHeading}
                </p>
                <h2 className="mt-3 font-display text-[26px] leading-[1.15] font-medium text-ink-700 md:text-[40px]">
                  {listingsCount > 0 ? (
                    <>
                      <span className="font-display italic">
                        {listingsCount}
                      </span>{' '}
                      <span>
                        {t('PROFILE.LISTINGS_COUNT', {
                          count: listingsCount
                        }).replace(/^\d+\s*/, '')}
                      </span>
                    </>
                  ) : (
                    <span>{t('PROFILE.NO_LISTINGS')}</span>
                  )}
                </h2>
              </div>
              <p className="max-w-md text-[14px] leading-relaxed text-ink-200 md:col-span-7 md:self-end md:text-[15px]">
                {listingsCount > 0
                  ? listingsIntro
                  : t('PROFILE.NO_LISTINGS_HINT')}
              </p>
            </div>

            {listingsUser.length > 0 && (
              <ul className="mt-10 grid grid-cols-1 gap-5 md:mt-14 md:grid-cols-2 md:gap-6">
                {listingsUser.map((spot) => (
                  <li key={spot.id}>
                    <ProfileLocationCard location={spot} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Reviews — its own editorial section */}
        {reviewsTotal > 0 && (
          <div className="pb-20 md:pb-28">
            <Reviews user={user} />
          </div>
        )}
      </div>

      <ReportUserModal
        isOpen={reportModalOpen}
        setIsOpen={setReportModalOpen}
        user={user}
      />
    </div>
  )
}
